// Derivation layer for the manager "Command Central" view.
//
// Pure functions only — no supabase calls, no React. Ported from the
// Fieldpoint prototype's lib/commandCenter.js onto TraverseClean's real
// schema (buildings/building_id instead of sites/site_id, no site status
// scoring reinvented). Takes the same raw rows TonightView already derives
// from (today's checklist_submissions, plus the same "who's expected today"
// map TonightView builds via buildExpectedMap) and turns them into three
// things a manager actually watches:
//   1. map markers (one per on-duty worker, colour = status)
//   2. a routine status board (on-site / finished — the boring, expected stuff)
//   3. an exceptions queue (needs-attention items, severity-sorted)
//
// Deliberately reuses TonightView's own isGpsMismatch / isGpsUnknown /
// buildExpectedMap (passed in by the caller) rather than reimplementing
// them here — the brief is explicit that this view must never silently
// disagree with Today about who's on-site, who's missing, or what counts
// as an off-site GPS reading.

export const SEVERITY = { CRITICAL: 'critical', WARNING: 'warning', INFO: 'info' };

const SEVERITY_RANK = { [SEVERITY.CRITICAL]: 0, [SEVERITY.WARNING]: 1, [SEVERITY.INFO]: 2 };

// A full shift with no check-out logged yet is worth flagging as a likely
// missed check-out rather than assuming the worker is just having a long day.
const MISSED_CHECKOUT_HOURS = 10;

function hoursSince(iso) {
  if (!iso) return 0;
  return (Date.now() - new Date(iso).getTime()) / 3600000;
}

// The roster's building_group codes (PDIC_RPYCC, RPYCC) map to the same real
// building IDs TonightView's ROSTER_GROUP_BUILDING uses, but the roster can
// cover more than one building per group (e.g. PDIC_RPYCC = both E and F) —
// this local copy exists only because ROSTER_GROUP_BUILDING (in TonightView)
// only keeps the first/primary building per group for its own purposes;
// duplicating the full set here doesn't change what Today shows, since this
// function is only ever used for start-time gating, never for who counts as
// expected.
const ROSTER_GROUP_BUILDINGS = { PDIC_RPYCC: ['E'], PDIC: ['E'], RPYCC: ['E'] };

/**
 * Earliest scheduled start time today for each expected worker, as a plain
 * "HH:MM:SS" string (comparable directly against a local HH:MM:SS clock
 * reading), or `null` when no source gives a specific time (e.g. a special
 * event with only a date, or a rental window marked "anytime") — a `null`
 * means "don't gate this worker on time," never "gate them out entirely,"
 * so nobody legitimately expected disappears just because we can't pin down
 * when.
 *
 * A worker with only "anytime" or undated expectations is intentionally
 * never gated — the whole point of "anytime" is that there's no specific
 * time to be late against.
 */
export function computeStartTimeByWorker({ roster, rentals, buildingTimes, todayDow }) {
  const startTimes = new Map(); // workerId -> Set(HH:MM:SS)
  function add(workerId, time) {
    if (!workerId || !time) return;
    if (!startTimes.has(workerId)) startTimes.set(workerId, new Set());
    startTimes.get(workerId).add(time);
  }

  for (const r of roster || []) {
    const buildingIds = ROSTER_GROUP_BUILDINGS[r.building_group] || [];
    for (const buildingId of buildingIds) {
      const bt = buildingTimes?.get(buildingId);
      if (!bt) continue;
      const start = r.shift_type === 'night' ? bt.night_start : bt.day_start;
      add(r.worker_id, start);
    }
  }

  for (const r of rentals || []) {
    const windows = Array.isArray(r.windows) ? r.windows : [];
    for (const w of windows) {
      if (w?.day !== todayDow) continue;
      if (w.type === 'time' && w.start) {
        if (w.worker_id) add(w.worker_id, w.start);
        else for (const wid of r.assigned_workers || []) add(wid, w.start);
      }
      // type === 'anytime' contributes no specific time on purpose.
    }
  }

  const result = new Map();
  for (const [workerId, times] of startTimes) {
    result.set(workerId, [...times].sort()[0]); // earliest "HH:MM:SS" sorts first as a plain string
  }
  return result;
}

function nowTimeStr() {
  const d = new Date();
  return [d.getHours(), d.getMinutes(), d.getSeconds()].map(n => String(n).padStart(2, '0')).join(':');
}

function elapsedLabel(iso) {
  const mins = Math.max(0, Math.round(hoursSince(iso) * 60));
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
}

/**
 * Builds the full Command Central data set from raw rows.
 *
 * @param {Array} subs - checklist_submissions within the lookback window (today's activity)
 * @param {Map} expected - workerId -> { id, full_name, buildings: Set } — from
 *   TonightView's buildExpectedMap, passed in so both views agree on who's expected.
 * @param {Array} buildings - the BUILDINGS constant (id, name, shortName, lat, lng, status)
 * @param {Array} issues - unresolved maintenance rows (checklist_submissions with
 *   maintenance_flag && !maintenance_resolved), fetched unbounded like TonightView does.
 * @param {(row) => boolean} isGpsMismatch - TonightView's own GPS-mismatch check
 * @param {(row) => boolean} isGpsUnknown - TonightView's own GPS-unknown check
 * @param {Map} [startTimeByWorker] - workerId -> "HH:MM:SS" earliest scheduled
 *   start today, from computeStartTimeByWorker — a worker with no entry (or
 *   an entry that's undefined) is never gated, so an unresolvable schedule
 *   still surfaces rather than going silent.
 * @param {string} today - local YYYY-MM-DD date string for "today", so a
 *   shift finished last night (included in `subs` for visibility) is never
 *   mistaken for today's own activity.
 */
// Local calendar date (not UTC) of a timestamp — a night shift's sign_in
// can be "yesterday" in wall-clock terms right up until just before
// midnight, and reading the UTC date instead would misclassify it for
// several hours every evening.
function localDateOf(iso) {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function buildCommandCenterData({ subs, expected, buildings, issues, isGpsMismatch, isGpsUnknown, startTimeByWorker, today }) {
  const buildingById = new Map(buildings.map(b => [b.id, b]));

  // worker_id -> submissions[] (today's activity, most recent first — subs already ordered)
  const subsByWorker = new Map();
  for (const row of subs) {
    if (!row.worker_id) continue;
    if (!subsByWorker.has(row.worker_id)) subsByWorker.set(row.worker_id, []);
    subsByWorker.get(row.worker_id).push(row);
  }

  // building_id -> Set(workerId expected there today) — the "is anyone else
  // covering this" consequence check for no-shows.
  const workersByBuilding = new Map();
  for (const [workerId, exp] of expected) {
    for (const bId of exp.buildings) {
      if (!workersByBuilding.has(bId)) workersByBuilding.set(bId, new Set());
      workersByBuilding.get(bId).add(workerId);
    }
  }

  const markers = [];
  const onSite = [];
  const finished = [];
  const exceptions = [];

  // Anyone with a real check-in today must show up here even if they
  // weren't on the expected roster (a pickup shift, an unscheduled swap,
  // a schedule that hasn't been updated yet) — Today shows these workers
  // by deriving straight from real submissions, so Command Central must
  // never silently drop someone Today would show. Build a synthetic
  // "expected" entry for anyone checked in today who isn't already in the
  // real expected map, rather than iterating the expected map alone.
  const allWorkerIds = new Set(expected.keys());
  for (const workerId of subsByWorker.keys()) {
    if (!allWorkerIds.has(workerId)) allWorkerIds.add(workerId);
  }

  for (const workerId of allWorkerIds) {
    const exp = expected.get(workerId) || {
      id: workerId,
      full_name: (subsByWorker.get(workerId) || [])[0]?.worker_name || 'Worker',
      buildings: new Set(),
    };
    const workerSubs = subsByWorker.get(workerId) || [];
    const openSub = workerSubs.find(r => !r.sign_out);
    // Today's own activity only — yesterday's already-closed shifts (kept in
    // `subs` so they can still surface under Finished below) must never
    // count as "did today's expected shift", or a no-show today would
    // silently disappear behind a completed shift from the night before.
    const todaySubs = workerSubs.filter(r => localDateOf(r.sign_in) === today);
    const expectedBuildingIds = [...exp.buildings];
    const primaryBuildingId = expectedBuildingIds[0];
    const primaryBuilding = buildingById.get(primaryBuildingId);
    // Other buildings *this* worker is scheduled at today — the "how many
    // more jobs today" consequence check for off-site/late flags.
    const otherJobsToday = expectedBuildingIds.length - 1;

    // Other workers expected anywhere this worker is expected today.
    const coverage = new Set();
    for (const bId of expectedBuildingIds) {
      for (const wid of (workersByBuilding.get(bId) || [])) {
        if (wid !== workerId) coverage.add(wid);
      }
    }

    if (openSub) {
      // Currently on-site (checked in, no check-out yet).
      const building = buildingById.get(openSub.building_id) || primaryBuilding;
      const mismatch = isGpsMismatch(openSub);
      const unknown = isGpsUnknown(openSub);
      const status = mismatch ? 'red' : unknown ? 'yellow' : 'green';

      const lat = openSub.gps_lat ?? building?.lat ?? null;
      const lng = openSub.gps_lng ?? building?.lng ?? null;

      markers.push({
        workerId, name: exp.full_name, buildingId: openSub.building_id,
        buildingName: building?.shortName || building?.name || openSub.building_id,
        status, lat, lng, subId: openSub.id, gpsVerified: openSub.gps_verified,
        distance: openSub.gps_distance_metres, signIn: openSub.sign_in,
      });

      const boardRow = {
        workerId, name: exp.full_name, buildingId: openSub.building_id,
        buildingName: building?.shortName || building?.name || openSub.building_id,
        signIn: openSub.sign_in, subId: openSub.id, gpsVerified: openSub.gps_verified,
        gpsMismatch: mismatch, gpsUnknown: unknown, distance: openSub.gps_distance_metres,
      };

      if (mismatch) {
        exceptions.push({
          id: `breach_${openSub.id}`, type: 'geofence_breach', severity: SEVERITY.CRITICAL,
          workerId, name: exp.full_name, buildingId: openSub.building_id, buildingName: building?.shortName || building?.name,
          title: `${exp.full_name} — off-site`,
          detail: `Checked in ${elapsedLabel(openSub.sign_in)} ago, about ${openSub.gps_distance_metres}m from ${building?.shortName || building?.name || 'the building'}`
            + (otherJobsToday > 0 ? ` — ${otherJobsToday} more job${otherJobsToday === 1 ? '' : 's'} scheduled today` : ''),
          sortKey: hoursSince(openSub.sign_in),
        });
      } else if (unknown) {
        exceptions.push({
          id: `gpsunknown_${openSub.id}`, type: 'gps_unknown', severity: SEVERITY.WARNING,
          workerId, name: exp.full_name, buildingId: openSub.building_id, buildingName: building?.shortName || building?.name,
          title: `${exp.full_name} — no GPS reading`,
          detail: `Checked in ${elapsedLabel(openSub.sign_in)} ago at ${building?.shortName || building?.name || 'building'}, no location came through`
            + (openSub.gps_error ? ` (${openSub.gps_error})` : '') + ' — verify manually.',
          sortKey: hoursSince(openSub.sign_in),
        });
      }

      if (hoursSince(openSub.sign_in) > MISSED_CHECKOUT_HOURS) {
        exceptions.push({
          id: `missedout_${openSub.id}`, type: 'missed_checkout', severity: SEVERITY.WARNING,
          workerId, name: exp.full_name, buildingId: openSub.building_id, buildingName: building?.shortName || building?.name,
          title: `${exp.full_name} — possible missed check-out`,
          detail: `Still shows checked in after ${elapsedLabel(openSub.sign_in)} at ${building?.shortName || building?.name || 'building'} — confirm they actually clocked out.`,
          sortKey: hoursSince(openSub.sign_in),
        });
      }

      onSite.push(boardRow);
    } else if (todaySubs.length > 0) {
      // Every submission today is closed out — finished for the day.
      const latest = todaySubs[0];
      const building = buildingById.get(latest.building_id) || primaryBuilding;
      finished.push({
        workerId, name: exp.full_name, buildingId: latest.building_id,
        buildingName: building?.shortName || building?.name || latest.building_id,
        signIn: latest.sign_in, signOut: latest.sign_out, subId: latest.id,
        gpsVerified: latest.gps_verified, gpsMismatch: isGpsMismatch(latest), gpsUnknown: isGpsUnknown(latest),
        distance: latest.gps_distance_metres,
      });
    } else if (expected.has(workerId)) {
      // Expected today, nothing logged at all — but only a real no-show once
      // their shift was actually supposed to have started. A worker whose
      // night shift starts at 5:30pm isn't late at 2pm; flagging them CRITICAL
      // hours before they're due is exactly the false-alarm pattern worth
      // avoiding. No specific start time on file (an undated special event, or
      // a rental window marked "anytime") means never gate — better to
      // surface a real no-show than hide one for lack of a timestamp.
      const startTime = startTimeByWorker?.get(workerId);
      if (startTime && nowTimeStr() < startTime) continue;

      const noCoverage = coverage.size === 0;
      exceptions.push({
        id: `noshow_${workerId}`, type: 'no_show', severity: noCoverage ? SEVERITY.CRITICAL : SEVERITY.WARNING,
        workerId, name: exp.full_name, buildingId: primaryBuildingId, buildingName: primaryBuilding?.shortName || primaryBuilding?.name,
        title: `${exp.full_name} — no-show`,
        detail: noCoverage
          ? `Scheduled at ${primaryBuilding?.shortName || primaryBuilding?.name || 'building'} today — no other worker is covering this building.`
          : `Scheduled at ${primaryBuilding?.shortName || primaryBuilding?.name || 'building'} today — ${coverage.size} other worker${coverage.size === 1 ? ' is' : 's are'} also assigned here.`,
        sortKey: 999, // no timestamp to rank by — always float to the top of its tier
      });

      markers.push({
        workerId, name: exp.full_name, buildingId: primaryBuildingId,
        buildingName: primaryBuilding?.shortName || primaryBuilding?.name || primaryBuildingId,
        status: 'red', lat: primaryBuilding?.lat ?? null, lng: primaryBuilding?.lng ?? null,
        subId: null, gpsVerified: null, distance: null, signIn: null, noShow: true,
      });
    }
  }

  // Last night's finished shifts, shown separately from today's own status
  // above — a shift that wrapped up after midnight is still worth seeing
  // all through today rather than vanishing the moment its calendar day
  // ends. Deliberately independent of whatever today's status was (on-site,
  // no-show, or nothing yet): finishing a shift last night and being late
  // for a different one today are both true at once, not a contradiction.
  for (const [workerId, workerSubs] of subsByWorker) {
    const priorClosed = workerSubs.find(r => r.sign_out && localDateOf(r.sign_in) !== today);
    if (!priorClosed) continue;
    const exp = expected.get(workerId);
    const name = exp?.full_name || workerSubs.find(r => r.worker_name)?.worker_name || 'Worker';
    const building = buildingById.get(priorClosed.building_id);
    finished.push({
      workerId, name, buildingId: priorClosed.building_id,
      buildingName: building?.shortName || building?.name || priorClosed.building_id,
      signIn: priorClosed.sign_in, signOut: priorClosed.sign_out, subId: priorClosed.id,
      gpsVerified: priorClosed.gps_verified, gpsMismatch: isGpsMismatch(priorClosed), gpsUnknown: isGpsUnknown(priorClosed),
      distance: priorClosed.gps_distance_metres,
    });
  }

  // Unresolved maintenance flags — real, needs-attention items, but not an
  // attendance/GPS problem, so they sit at the lowest severity tier rather
  // than competing with real-time staffing exceptions.
  for (const row of issues || []) {
    const building = buildingById.get(row.building_id);
    exceptions.push({
      id: `maint_${row.id}`, type: 'maintenance', severity: SEVERITY.INFO, subId: row.id,
      workerId: row.worker_id || null, name: row.worker_name, buildingId: row.building_id, buildingName: building?.shortName || building?.name,
      title: `Reported issue — ${building?.shortName || building?.name || row.building_id}`,
      detail: row.maintenance_note || 'No details given.',
      sortKey: hoursSince(row.sign_in),
    });
  }

  exceptions.sort((a, b) => {
    const rankDiff = SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity];
    if (rankDiff !== 0) return rankDiff;
    return (b.sortKey ?? 0) - (a.sortKey ?? 0);
  });

  onSite.sort((a, b) => a.name.localeCompare(b.name));
  finished.sort((a, b) => a.name.localeCompare(b.name));

  const activeBuildings = buildings.filter(b => b.status === 'active' && b.lat != null && b.lng != null);

  return { markers, onSite, finished, exceptions, activeBuildings };
}
